import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { toast } from 'sonner';
import { Loading } from '../../components/office';
import { purchaseOrdersApi, stockApi } from '@verlo/shared/services/api';
import { useAuth } from '@verlo/shared/contexts/AuthContext';
import { Button } from '@verlo/shared/components/ui/button';
import { Input } from '@verlo/shared/components/ui/input';
import { Notice } from '@verlo/shared/components/ui/notice';
import { Signal } from '@verlo/shared/components/ui/signal';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@verlo/shared/components/ui/select';
import { qty, stockWords } from '../../lib/orders';

// Receive a delivery against its order (Back office sweep, Oct 2026: D10, mock "Receive").
// The order's lines are filled in: tick what came, change anything that didn't match. Their
// delivery-note or invoice number goes on it, to match the invoice against. Receiving raises
// stock by what came, at the price it came at, and keeps it as a delivery.

const money = (v) => new Intl.NumberFormat('en-GB', { style: 'currency', currency: 'GBP' }).format(v || 0);

export default function ReceiveOrderPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [order, setOrder] = useState(null);
  const [stock, setStock] = useState([]);
  const [came, setCame] = useState({});        // stock_id → { quantity, note }
  const [extras, setExtras] = useState([]);    // [{ stock_id, quantity, price }]
  const [theirRef, setTheirRef] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    Promise.all([purchaseOrdersApi.get(id), stockApi.getAll()]).then(([o, s]) => {
      setOrder(o.data);
      setStock(s.data || []);
      // It starts from what they said they'd send, where they've confirmed (Oct 2026).
      setCame(Object.fromEntries(o.data.lines.map((l) => [l.stock_id, { quantity: l.confirmed ?? l.quantity, note: '' }])));
    }).catch(() => toast.error("Couldn't load the order"));
  }, [id]);

  const byId = useMemo(() => Object.fromEntries(stock.map((s) => [s.id, s])), [stock]);
  if (!order) return <Loading />;
  if (order.status !== 'sent' && order.status !== 'draft') {
    return (
      <div className="mx-auto max-w-3xl space-y-3">
        <Link to={`/back-office/orders/${order.id}`} className="text-[13px] text-ink-soft hover:text-ink">‹ {order.number}</Link>
        <Notice tone="info" word="Received">This order has been received already.</Notice>
      </div>
    );
  }

  const lines = order.lines.map((l) => ({ ...l, received: Number(came[l.stock_id]?.quantity ?? l.quantity), note: came[l.stock_id]?.note || '' }));
  const ordered = lines.reduce((t, l) => t + l.quantity * l.price, 0);
  const due = lines.reduce((t, l) => t + l.received * l.price, 0) + extras.reduce((t, e) => t + Number(e.quantity || 0) * Number(e.price || 0), 0);
  const words = stockWords([...lines, ...extras.map((e) => ({ ...e, name: byId[e.stock_id]?.name, received: e.quantity }))], byId);
  const stays = lines.filter((l) => !l.received).map((l) => `${l.name} stays at ${qty(byId[l.stock_id]?.quantity, l.unit)}.`);
  const others = stock.filter((s) => (!s.site_id || s.site_id === order.site_id) && !order.lines.some((l) => l.stock_id === s.id) && !extras.some((e) => e.stock_id === s.id));

  const receive = async () => {
    setSaving(true);
    try {
      await purchaseOrdersApi.receive(order.id, {
        their_ref: theirRef.trim(),
        lines: [
          ...lines.map((l) => ({ stock_id: l.stock_id, quantity: l.received, note: l.note })),
          ...extras.filter((e) => Number(e.quantity) > 0).map((e) => ({ stock_id: e.stock_id, quantity: Number(e.quantity), price: Number(e.price) || 0 })),
        ],
      });
      toast.success(`Received from ${order.supplier_name}; stock is up`);
      navigate(`/back-office/orders/${order.id}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't receive it");
    } finally { setSaving(false); }
  };

  return (
    <div className="mx-auto max-w-4xl space-y-4" data-testid="receive-page">
      <Link to={`/back-office/orders/${order.id}`} className="text-[13px] text-ink-soft hover:text-ink">‹ Orders</Link>
      <div>
        <h1 className="font-heading text-[26px] font-extrabold text-ink">Receive from {order.supplier_name}</h1>
        <p className="text-[14px] text-ink-soft">Order <b className="font-heading font-bold text-ink">{order.number}</b>{order.sent_at ? `, sent ${new Date(order.sent_at).toLocaleDateString('en-GB', { weekday: 'long', day: 'numeric', month: 'long' })}` : ''}. Tick what came; change anything that didn't match.</p>
      </div>

      <div className="flex flex-wrap items-end gap-4">
        <label className="w-72 space-y-1.5"><span className="text-[12.5px] text-ink-soft">Their delivery note or invoice number</span>
          <Input value={theirRef} onChange={(e) => setTheirRef(e.target.value)} maxLength={60} placeholder="e.g. MD-58817" data-testid="receive-their-ref" /></label>
        <span className="pb-2 text-[12.5px] text-ink-faint">Optional. Their number is what you match the invoice against.</span>
      </div>

      <div className="overflow-x-auto rounded-lg border border-hairline bg-white">
        <table className="w-full min-w-[640px] text-[14px]">
          <thead><tr className="border-b border-hairline bg-surface-2 text-left font-heading text-[11px] font-bold uppercase tracking-[0.08em] text-ink-faint">
            <th className="w-12 px-4 py-2" /><th className="px-2 py-2">Item</th><th className="px-3 py-2">Ordered</th><th className="px-3 py-2">Came</th><th className="px-3 py-2">Note</th></tr></thead>
          <tbody>
            {lines.map((l) => {
              const want = l.confirmed ?? l.quantity;
              const full = l.received >= want;
              const set = (patch) => setCame((c) => ({ ...c, [l.stock_id]: { ...c[l.stock_id], ...patch } }));
              return (
                <tr key={l.stock_id} className={`border-b border-hairline last:border-0 ${full ? '' : 'bg-signal-warn-tint/50'}`} data-testid={`receive-line-${l.stock_id}`}>
                  <td className="px-4 py-2.5">
                    <button type="button" role="checkbox" aria-checked={full} aria-label={`${l.name} came in full`}
                      onClick={() => set({ quantity: full ? 0 : want })}
                      className={`grid h-6 w-6 place-items-center rounded border-2 text-[13px] text-white ${full ? 'border-verlo bg-verlo' : 'border-hairline-strong bg-white'}`}>{full ? '✓' : ''}</button>
                  </td>
                  <td className="px-2 py-2.5 text-ink">{l.name}</td>
                  <td className="px-3 py-2.5 font-mono tabular-nums text-ink-soft">{qty(l.quantity, l.unit)}
                    {want < l.quantity && <span className="block font-sans text-[12px] text-ink-faint">they said {qty(want, l.unit)}</span>}</td>
                  <td className="px-3 py-2.5">
                    <div className="flex items-center gap-1.5">
                      <Input type="number" min="0" step="any" value={came[l.stock_id]?.quantity ?? ''} onChange={(e) => set({ quantity: e.target.value === '' ? 0 : Number(e.target.value) })}
                        className="h-9 w-24 font-mono tabular-nums" aria-label={`${l.name} came`} data-testid={`receive-came-${l.stock_id}`} />
                      <span className="text-[13px] text-ink-soft">{l.unit !== 'each' ? l.unit : ''}</span>
                    </div>
                  </td>
                  <td className="px-3 py-2.5">
                    {!full ? (
                      <div className="flex items-center gap-2"><Signal tone="warn" dot={false}>short</Signal>
                        <Input value={l.note} onChange={(e) => set({ note: e.target.value })} placeholder="e.g. Out of stock, next week" className="h-9" maxLength={200} aria-label={`Why ${l.name} was short`} /></div>
                    ) : <span className="text-[13px] text-ink-faint">—</span>}
                  </td>
                </tr>
              );
            })}
            {extras.map((e, i) => (
              <tr key={e.stock_id} className="border-b border-hairline">
                <td className="px-4 py-2.5 text-[12px] text-ink-faint">extra</td>
                <td className="px-2 py-2.5 text-ink">{byId[e.stock_id]?.name}</td>
                <td className="px-3 py-2.5 text-ink-faint">—</td>
                <td className="px-3 py-2.5"><Input type="number" min="0" step="any" value={e.quantity} onChange={(ev) => setExtras(extras.map((x, j) => (j === i ? { ...x, quantity: ev.target.value } : x)))} className="h-9 w-24 font-mono tabular-nums" aria-label="Quantity that came" /></td>
                <td className="px-3 py-2.5"><div className="flex items-center gap-2"><span className="text-[13px] text-ink-soft">at £</span>
                  <Input type="number" min="0" step="0.01" value={e.price} onChange={(ev) => setExtras(extras.map((x, j) => (j === i ? { ...x, price: ev.target.value } : x)))} className="h-9 w-24 font-mono tabular-nums" aria-label="Price each" />
                  <button type="button" className="text-ink-faint hover:text-signal-stop" onClick={() => setExtras(extras.filter((_, j) => j !== i))} aria-label="Take it off">×</button></div></td>
              </tr>
            ))}
          </tbody>
        </table>
        <div className="border-t border-hairline px-4 py-2.5">
          <Select value="" onValueChange={(sid) => setExtras([...extras, { stock_id: sid, quantity: 1, price: byId[sid]?.cost_per_unit || 0 }])}>
            <SelectTrigger className="h-9 w-64" data-testid="receive-extra"><SelectValue placeholder="+ Something else came" /></SelectTrigger>
            <SelectContent>{others.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}</SelectContent>
          </Select>
        </div>
      </div>

      {(words.length > 0 || stays.length > 0) && (
        <Notice tone="info" word="Stock" data-testid="receive-stock-words">
          {words.length > 0 && <b className="font-semibold">{words.join(' ')}</b>} {stays.join(' ')}
          {Math.abs(ordered - due) >= 0.01 && <> The invoice should be {money(Math.abs(ordered - due))} {due < ordered ? 'less' : 'more'} than the order: {money(due)}.</>}
        </Notice>
      )}

      <div className="flex items-center justify-between">
        <span className="text-[13px] text-ink-soft">Received by {user?.name || user?.email}</span>
        <Button className="bg-verlo hover:bg-verlo-hover" onClick={receive} disabled={saving} data-testid="receive-save">{saving ? 'Receiving…' : 'Receive and add to stock'}</Button>
      </div>
    </div>
  );
}
